import React, { useState } from 'react';
import { ChevronUp, ChevronDown, AlertCircle, Plus, Eye, Save } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { slideRegistry } from '../../../core/plugin-system/SlideRegistry';
import type { LessonDTO, LessonTypeConfig } from '../../../core/types/lesson.types';
import { cn } from '../../../../../shared/utils';

interface CreationSidebarProps {
  lesson: LessonDTO | null;
  config: LessonTypeConfig;
  allowedSlideTypes: string[];
  canSave: boolean;
  isSaving: boolean;
  isDirty: boolean;
  slideCount: number;
  errors: Record<string, any>;
  onAddSlide: (type: string) => void;
  onSave: () => void;
  onPreview: () => void;
}

export const CreationSidebar: React.FC<CreationSidebarProps> = ({
  lesson,
  config,
  allowedSlideTypes,
  canSave,
  isSaving,
  isDirty,
  slideCount,
  errors,
  onAddSlide,
  onSave,
  onPreview,
}) => {
  const [isSlideMenuExpanded, setIsSlideMenuExpanded] = useState(true);

  const hasErrors = Object.keys(errors).length > 0;
  const isAtMaxSlides = config.features.maxSlides && slideCount >= config.features.maxSlides;
  const isUnderMinSlides = config.features.minSlides && slideCount < config.features.minSlides;

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 mb-2">
          <config.icon className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">Editor</h3>
        </div>
        {lesson && (
          <p className="text-xs text-muted-foreground truncate" title={lesson.title}>
            {lesson.title || 'Lecție nouă'}
          </p>
        )}
      </div>

      {/* Slide Count Info */}
      <div className="p-4 bg-muted border-b">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Slide-uri:</span>
          <span className="font-semibold text-foreground">
            {slideCount}
            {config.features.maxSlides && ` / ${config.features.maxSlides}`}
          </span>
        </div>
        {config.features.minSlides && isUnderMinSlides && (
          <p className="mt-2 text-xs text-orange-600 flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Minim {config.features.minSlides} slide-uri necesare</span>
          </p>
        )}
        {isAtMaxSlides && (
          <p className="mt-2 text-xs text-primary">
            Numărul maxim de slide-uri a fost atins
          </p>
        )}
      </div>

      {/* Add Slide Section */}
      <div className="flex-1 overflow-y-auto ecotic-table-scrollbar">
        <div className="p-4">
          <button
            onClick={() => setIsSlideMenuExpanded(!isSlideMenuExpanded)}
            className="w-full flex items-center justify-between text-sm font-medium text-foreground mb-3 hover:text-foreground"
          >
            <span>Adaugă slide</span>
            {isSlideMenuExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {isSlideMenuExpanded && (
            <div className="space-y-2">
              {allowedSlideTypes.map((slideType) => {
                const plugin = slideRegistry.get(slideType);
                if (!plugin) return null;

                return (
                  <Button
                    key={slideType}
                    onClick={() => onAddSlide(slideType)}
                    disabled={isAtMaxSlides || isSaving}
                    variant="outline"
                    className={cn(
                      'w-full justify-start gap-2 h-auto py-3',
                      'hover:bg-primary/10 hover:border-blue-300'
                    )}
                  >
                    <plugin.icon className="w-4 h-4 shrink-0" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium">{plugin.displayName}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2 whitespace-normal">
                        {plugin.description}
                      </div>
                    </div>
                    <Plus className="w-4 h-4 shrink-0" />
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        {/* Validation Errors */}
        {hasErrors && (
          <div className="p-4 border-t border-border">
            <div className="p-3 bg-destructive/10 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 mb-1">
                    Erori de validare
                  </p>
                  <ul className="text-xs text-destructive space-y-1">
                    {Object.entries(errors).slice(0, 3).map(([key, errorArray]) => (
                      <React.Fragment key={key}>
                        {(Array.isArray(errorArray) ? errorArray : [errorArray]).map((error, idx) => (
                          <li key={`${key}-${idx}`} className="break-words">
                            • {error}
                          </li>
                        ))}
                      </React.Fragment>
                    ))}
                    {Object.keys(errors).length > 3 && (
                      <li className="text-destructive font-medium">
                        +{Object.keys(errors).length - 3} erori
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-4 border-t border-border space-y-2">
        {/* Preview Button */}
        <Button
          onClick={onPreview}
          disabled={!lesson || slideCount === 0 || isSaving}
          variant="outline"
          className="w-full gap-2"
        >
          <Eye className="w-4 h-4" />
          Previzualizare
        </Button>

        {/* Save Button */}
        <Button
          onClick={onSave}
          disabled={!canSave || isSaving}
          className={cn(
            'w-full gap-2',
            isDirty && !hasErrors && 'animate-pulse'
          )}
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Se salvează...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Salvează lecția
            </>
          )}
        </Button>

        {/* Dirty Indicator */}
        {isDirty && !isSaving && (
          <p className="text-xs text-center text-orange-600">
            Modificări nesalvate
          </p>
        )}
      </div>
    </div>
  );
};

export default CreationSidebar;

